import React from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";

const sections = [
  {
    title: "Introduction",
    content:
      'Axtelica Solutions Pvt Ltd ("Axtelica", "we", "our", or "us") values your privacy and is committed to protecting your personal information and business data. This Privacy Policy explains how we collect, use, process, store, and protect information when you access our website, software platforms, products, and services, including AI-powered SaaS applications operated by Axtelica. By using our services, you agree to the practices described in this Privacy Policy.',
  },
  {
    title: "Information We Collect",
    content:
      "We may collect Personal Information such as Name, Email address, Phone number, Company name, Job title, Billing details, and Account credentials. We also collect Technical Information like IP address, Browser type, Device information, Operating system, Login timestamps, Usage analytics, Cookies and tracking data. Additionally, we collect Business and Platform Data such as uploaded files and datasets, reports and dashboards, analytics configurations, user-generated content, and API usage data.",
  },
  {
    title: "How We Use Information",
    content:
      "We use collected information to provide and improve services, manage user accounts, process subscriptions and payments, deliver customer support, improve platform security, analyze performance, prevent fraud and abuse, send service-related communications, and comply with legal obligations. We do not sell personal or customer data to third parties.",
  },
  {
    title: "Data Processing and AI Services",
    content:
      "Some Axtelica products use AI and automated systems to analyze uploaded data, generate insights, detect anomalies, provide recommendations, and improve workflows. AI outputs depend on user data and may not always be fully accurate. Users are responsible for validating outputs before business decisions.",
  },
  {
    title: "Data Sharing",
    content:
      "We may share information only with authorized service providers, cloud hosting providers, payment processing partners, and legal or regulatory authorities when required by law. All partners must maintain confidentiality and security standards. We do not share data for advertising or resale.",
  },
  {
    title: "Data Security",
    content:
      "We implement commercially reasonable safeguards including HTTPS/SSL encryption, access controls, secure authentication systems, firewalls, monitoring, and rate limiting. However, no system can guarantee absolute security.",
  },
  {
    title: "Data Retention",
    content:
      "We retain information as long as necessary for service delivery, legal compliance, security, backups, disaster recovery, and contractual obligations. Data may be deleted or anonymized after retention periods.",
  },
  {
    title: "User Rights",
    content:
      "Users may have rights to access, correct, delete, or restrict processing of their personal data and withdraw consent where applicable, depending on applicable laws. Requests can be submitted via contact details provided below.",
  },
  {
    title: "International Data Transfers",
    content:
      "Your information may be processed or stored in different countries depending on infrastructure and service providers. By using our services, you consent to such transfers where legally permitted.",
  },
  {
    title: "Third-Party Links and Services",
    content:
      "Our platforms may include third-party links or integrations. We are not responsible for their privacy practices or content. Users should review third-party policies separately.",
  },
  {
    title: "Children's Privacy",
    content:
      "Our services are intended for business use and not directed toward children under 18. We do not knowingly collect data from minors.",
  },
  {
    title: "Policy Updates",
    content:
      "We may update this Privacy Policy periodically. Updated versions will be published with a revised effective date. Continued use of services means acceptance of changes.",
  },
  {
    title: "Contact Information",
    content: (
      <>
        <p className="mb-2">Axtelica Solutions Pvt Ltd.</p>

        <p className="mb-2 break-all">
          Email:{" "}
          <a
            href="mailto:info@axtelica.com"
            className="text-blue-600 hover:text-blue-800 underline transition"
          >
            info@axtelica.com
          </a>
        </p>

        <p className="break-all">
          Website:{" "}
          <a
            href="https://axtelica.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline transition"
          >
            Axtelica Official Website
          </a>
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="w-full overflow-hidden bg-white">
      {/* HERO SECTION */}
      <section
        className="
          relative flex items-center justify-center
          py-14 sm:py-16 lg:py-20
          min-h-[240px] sm:min-h-[320px] lg:min-h-[420px]
          w-full overflow-hidden
          bg-[linear-gradient(-45deg,#101030,#0F1242,#2558BA,#306BCA)]
          bg-[length:400%_400%]
          animate-gradientFlow
        "
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="relative z-10 w-full max-w-[1400px] px-4 sm:px-6 lg:px-8 flex justify-center"
        >
          <h1
            className="
              text-[30px] 
              sm:text-[42px] 
              md:text-[52px] 
              lg:text-[64px]
              font-semibold
              text-white
              text-center
              leading-tight
              max-w-4xl
            "
          >
            Privacy Policy
          </h1>
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <div className="w-full py-8 sm:py-10 lg:py-14 px-3 sm:px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="
            max-w-[1400px]
            mx-auto
            rounded-2xl
          "
        >
          {/* SUBTITLE */}
          <p
            className="
              text-center
              text-gray-700
              text-base
              sm:text-lg
              md:text-xl
              lg:text-2xl
              leading-relaxed
              mb-8
              sm:mb-10
              px-2
            "
          >
            Your privacy is important to us. Please read this policy carefully.
          </p>

          {/* SECTIONS */}
          <div className="space-y-5 sm:space-y-6">
            {sections.map((sec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                }}
                className="
                  border border-gray-200
                  rounded-2xl
                  p-4
                  sm:p-6
                  lg:p-7
                  bg-white
                  hover:shadow-lg
                  transition-all
                  duration-300
                "
              >
                {/* TITLE */}
                <h2
                  className="
                    text-xl
                    sm:text-2xl
                    lg:text-2xl
                    
                    text-gray-800
                    mb-3
                    leading-snug
                  "
                >
                  {sec.title}
                </h2>

                {/* CONTENT */}
                <div
                  className="
                    text-sm
                    sm:text-base
                    lg:text-lg
                    text-gray-600
                    leading-7
                    sm:leading-8
                    whitespace-pre-line
                  "
                >
                  {sec.content}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}